import { deepmerge } from "@fastify/deepmerge";
import { Task } from "@lit/task";
import { defaultDashboardConfig, type Employee, EmployeeStatus } from "@pentacode/core";
import type { Checkbox } from "@pentacode/ui/components";
import { css, html, LitElement, type TemplateResult } from "lit";
import { customElement, property, query } from "lit/decorators.js";
import { createRef, ref, type Ref } from "lit/directives/ref.js";
import "../../../../elements/absence-dialog";
import { app, router } from "../../../../init";
import { shared } from "../../../../styles";
import { DayMonthFormatter, getStartOfToday, userFormatSettings } from "../../../../utils";
import "../event-list";
import "./base-tile";
import type { DashboardTile } from "./base-tile";
import {
    getAnniversaryDatesForYears,
    getAnniversaryEvents,
    getAnniversaryLabel,
} from "./tile-birthdays-anniversaries.util";

export type AnniversaryTileEvent = {
    date: Temporal.PlainDateTime;
    data: Employee;
    type: "birthday" | "anniversary";
};

const mergeDashboardSettings = deepmerge();

@customElement("ptc-dashboard-tile-birthdays-anniversaries")
export class DashboardTileBirthdaysAnniversaries extends LitElement {
    @property({ attribute: false })
    days: [past: number, future?: number] = [
        app.settings.dashboard.tiles.birthdays.settings!.lookbehindDays,
        app.settings.dashboard.tiles.birthdays.settings!.lookaheadDays,
    ];

    @property({ attribute: false })
    size: { inline: number; block: number } = app.settings.dashboard.tiles.birthdays.size;

    private _tileEventsTask = new Task(this, {
        task: async () => {
            const currentYear = Temporal.Now.plainDateISO(userFormatSettings.timeZone).year;
            const activeEmployees = app.employees.filter((e) => e.status === EmployeeStatus.Active);
            const settings = app.settings.dashboard.tiles.birthdays.settings!;

            const birthdays: AnniversaryTileEvent[] = settings.showBirthdays
                ? activeEmployees
                      .filter((e): e is Employee & { birthday: string } => !!e.birthday)
                      .flatMap((employee) => {
                          const dates = getAnniversaryDatesForYears(employee.birthday, currentYear);
                          return Object.values(dates).map((date) => ({
                              date,
                              data: employee,
                              type: "birthday" as const,
                          }));
                      })
                : [];

            const anniversaries: AnniversaryTileEvent[] = settings.showAnniversaries
                ? activeEmployees
                      .filter((e) => e.firstContract?.start)
                      .flatMap((employee) => getAnniversaryEvents(employee, currentYear))
                : [];

            return [...birthdays, ...anniversaries];
        },
        args: () => [
            app.settings.dashboard.tiles.birthdays.settings!.showBirthdays,
            app.settings.dashboard.tiles.birthdays.settings!.showAnniversaries,
        ],
    });

    static styles = [
        shared,
        css`
            :host {
                display: contents;
            }

            ptc-dashboard-tile {
                --tile-icon-color: var(--purple);
            }

            .days-input {
                width: 6ch;
                text-align: right;
            }

            .tile-spinner {
                font-size: 1rem;
            }

            ptc-event-list::part(birthday-entry-wrapper) {
                display: flex;
                align-items: center;
                gap: 1rem;
                flex: 1;
                min-width: 0;
            }

            ptc-event-list::part(eventitem) {
                cursor: pointer;
            }

            ptc-event-list::part(eventitem):hover {
                --birthday-name-color: var(--blue);
            }

            ptc-event-list::part(birthday-name) {
                color: var(--birthday-name-color, inherit);
                transition: color 0.2s ease-in-out;
            }
        `,
    ];

    private _entryTemplate = (event: AnniversaryTileEvent): TemplateResult => {
        const routeTarget =
            event.type === "birthday"
                ? `employees/${event.data.id}/core-data`
                : `employees/${event.data.id}/contracts/view/${event.data.firstContract?.id}`;

        return html`
            <div part="birthday-entry-wrapper" @click=${() => router.go(routeTarget)}>
                <ptc-avatar .employee=${event.data}></ptc-avatar>
                <div class="layout vertical half-spacing">
                    <span class="semibold">${event.data.name}</span>
                    <span class="subtle"> ${getAnniversaryLabel(event)} </span>
                </div>
            </div>
        `;
    };

    private get _futureDays() {
        return this.days[1] ?? 7;
    }

    private get _tileLabel() {
        const settings = app.settings.dashboard.tiles.birthdays.settings!;
        if (settings.showBirthdays && settings.showAnniversaries) {
            return "Geburtstage & Jubiläen";
        }
        if (settings.showAnniversaries) {
            return "Jubiläen";
        }
        return "Geburtstage";
    }

    private _formRef: Ref<HTMLFormElement> = createRef();
    private _tileRef: Ref<DashboardTile> = createRef();

    @query("ptc-ui-checkbox[name='showBirthdays']")
    private _showBirthdaysCheckbox?: Checkbox;

    @query("ptc-ui-checkbox[name='showAnniversaries']")
    private _showAnniversariesCheckbox?: Checkbox;

    private _saveSettings(ev: SubmitEvent | InputEvent) {
        ev.preventDefault();

        const data = new FormData(this._formRef.value);

        const lookaheadDays = Number(data.get("lookaheadDays"));
        const lookbehindDays = Number(data.get("lookbehindDays"));
        const showBirthdays = this._showBirthdaysCheckbox?.checked ?? true;
        const showAnniversaries = this._showAnniversariesCheckbox?.checked ?? true;

        app.updateSettings({
            dashboard: mergeDashboardSettings(app.settings.dashboard, {
                tiles: {
                    birthdays: {
                        settings: { lookaheadDays, lookbehindDays, showBirthdays, showAnniversaries },
                    },
                },
            }),
        })
            .then(() => {
                this.requestUpdate();
                if (this._tileRef.value) {
                    this._tileRef.value.settingsOpen = false;
                }
            })
            .catch((e) => {
                console.error(e);
            });
    }

    private _renderSettings() {
        const tileSettings = app.settings.dashboard.tiles.birthdays.settings!;
        return html`
            <form slot="settings" ${ref(this._formRef)} @submit=${this._saveSettings} id="tile.birthdays">
                <fieldset>
                    <legend>Zeitraum</legend>
                    <div class="field">
                        <div class="input-container">
                            <input
                                name="lookaheadDays"
                                id="tile.birthdays.lookaheadDays"
                                type="number"
                                min="0"
                                max="365"
                                maxlength="3"
                                class="days-input"
                                placeholder=${defaultDashboardConfig.tiles.birthdays.settings!.lookaheadDays.toString()}
                                .value=${app.settings.dashboard.tiles.birthdays.settings!.lookaheadDays?.toString()}
                                .defaultValue=${defaultDashboardConfig.tiles.birthdays.settings!.lookaheadDays.toString()}
                            />
                            Tage in der Zukunft
                        </div>
                    </div>
                    <div class="field">
                        <div class="input-container">
                            <input
                                name="lookbehindDays"
                                id="tile.birthdays.lookbehindDays"
                                type="number"
                                min="0"
                                max="365"
                                maxlength="3"
                                class="days-input"
                                placeholder=${defaultDashboardConfig.tiles.birthdays.settings!.lookbehindDays.toString()}
                                .value=${app.settings.dashboard.tiles.birthdays.settings!.lookbehindDays.toString()}
                                .defaultValue=${defaultDashboardConfig.tiles.birthdays.settings!.lookbehindDays.toString()}
                            />
                            Tage in der Vergangenheit
                        </div>
                    </div>
                </fieldset>

                <fieldset>
                    <legend>Ereignisse anzeigen</legend>
                    <div class="field">
                        <label class="button ghost horizontal center-aligning spacing layout">
                            <span class="stretch ellipsis"
                                ><ptc-ui-icon name="icon.event.birthday"></ptc-ui-icon> Geburtstage</span
                            >
                            <ptc-ui-checkbox
                                class="non-interactive"
                                name="showBirthdays"
                                .checked=${tileSettings.showBirthdays ?? true}
                                .defaultChecked=${true}
                            ></ptc-ui-checkbox>
                        </label>
                    </div>
                    <div class="field">
                        <label class="button ghost horizontal center-aligning spacing layout">
                            <span class="stretch ellipsis"
                                ><ptc-ui-icon name="icon.event.anniversary"></ptc-ui-icon> Dienstjubiläen</span
                            >
                            <ptc-ui-checkbox
                                class="non-interactive"
                                name="showAnniversaries"
                                .checked=${tileSettings.showAnniversaries ?? true}
                                .defaultChecked=${true}
                            ></ptc-ui-checkbox>
                        </label>
                    </div>
                </fieldset>

                <!-- workaround for some browsers needing a explicit submit element -->
                <input type="submit" hidden />
            </form>
        `;
    }

    render() {
        return html`
            <ptc-dashboard-tile
                ${ref(this._tileRef)}
                label=${this._tileLabel}
                .subLabel=${`in den nächsten ${app.settings.dashboard.tiles.birthdays.settings!.lookaheadDays} Tagen`}
                .size=${this.size}
                .settingsForm=${this._formRef.value}
                tileType="birthdays"
                icon="icon.event.birthday"
                @tile-size-change=${(e: CustomEvent) => {
                    this.size = e.detail.size;
                }}
            >
                ${this._tileEventsTask.render({
                    initial: () => html``,
                    pending: () => html`
                        <ptc-ui-spinner class="double-padded tile-spinner place-self-center" active></ptc-ui-spinner>
                    `,
                    error: () =>
                        html`<div class="double-padded text-centering">
                            ${this._tileLabel} konnten nicht geladen werden!
                        </div>`,

                    complete: (events) => {
                        return html` <ptc-event-list
                            .timeWindow=${[
                                getStartOfToday().subtract({
                                    days: app.settings.dashboard.tiles.birthdays.settings!.lookbehindDays,
                                }),
                                getStartOfToday().add({
                                    days: app.settings.dashboard.tiles.birthdays.settings!.lookaheadDays,
                                }),
                            ]}
                            .dateFormat=${DayMonthFormatter}
                            mode="detailed"
                            .events="${events}"
                            .itemTemplate=${this._entryTemplate}
                            .useFallback=${false}
                        >
                            <div slot="empty-message" class="double-padded text-centering subtle place-self-center">
                                <span class="emoji huger">🎁</span>
                                <p>${this._getEmptyMessage()}</p>
                            </div>
                        </ptc-event-list>`;
                    },
                })}
                ${this._renderSettings()}
            </ptc-dashboard-tile>
        `;
    }

    private _getEmptyMessage() {
        if (this.days[0] === 0 && this._futureDays === 0) {
            return `Heute keine ${this._tileLabel}`;
        }

        return `Keine ${this._tileLabel} im gewählten Zeitraum`;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "ptc-dashboard-tile-birthdays-anniversaries": DashboardTileBirthdaysAnniversaries;
    }
}
